import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import ContentstackLivePreview from '@contentstack/live-preview-utils';
import { useLocation } from '@reach/router';
import { ContentstackGatsby } from 'gatsby-source-contentstack/live-preview';

import AnalyticsService from 'services/analyticsService';
import { Logger } from 'services/logger';
import { ONETRUST_CATEGORIES, OneTrustService } from 'services/oneTrust';
import { PersonalizeService } from 'services/personalize';
import { PersonalizeSDKError, PersonalizeSDKService } from 'services/personalize/sdkService';

import { getExperienceAndVariantFromAlias, triggerEvent } from 'utils/contentstack/personalize';
import { isLivePreviewEnabled, isPersonalizeEnabled } from 'utils/featureFlags';
import { LOCALES } from 'utils/localisationConfig';

import { getCSData } from '../live-preview';

export const AB_TEST_EVENTS = {
  BUTTON_CLICK: '[Block] Button: Click',
};

const ContentstackContext = createContext(null);

export const ContentstackProvider = ({ children, pageData, abTestConfiguration, personalizeVariantMeta }) => {
  const [data, setData] = useState(pageData);
  const [sdk, setSdk] = useState(null);
  const [personalizeExperiences, setPersonalizeExperiences] = useState([]);
  const [isPersonalizeDataSettled, setIsPersonalizeDataSettled] = useState(false);
  const [clickEventTriggers, setClickEventTriggers] = useState(abTestConfiguration?.click_event_triggers || []);

  const { search } = useLocation();

  const fetchLivePreviewData = async () => {
    try {
      const updatedData = await getCSData.get(data);
      setData(updatedData);
    } catch (error) {
      Logger.error('Error fetching live preview data:', error);
    }
  };

  const applyPersonalizeResult = (personalizedData, personalizeSdk) => {
    setData(personalizedData.data);
    setClickEventTriggers(
      personalizedData.data?.ab_test_configuration?.click_event_triggers ||
        abTestConfiguration?.click_event_triggers ||
        []
    );

    const aliases = personalizedData.aliases;

    void personalizeSdk.triggerImpressions({ aliases });

    const experiences = aliases.map((alias) => {
      const { experienceShortUid, variantShortUid } = getExperienceAndVariantFromAlias(alias);
      const meta = personalizeVariantMeta?.[alias];
      return {
        alias,
        experienceShortUid,
        variantShortUid,
        experience: meta?.experience_name ?? personalizedData.data?.experience_name,
        variant: meta?.variant_name ?? personalizedData.data?.variant_name,
      };
    });

    experiences.forEach(({ alias, experienceShortUid, variantShortUid, experience, variant }) => {
      AnalyticsService.pushEvent('ab_test_variation_viewed', {
        variation_alias: alias,
        variation_id: experienceShortUid,
        experience_name: experience,
        variation_name: variant,
        active_variant: variantShortUid,
      });
    });

    Logger.log('Trigger Personalize impressions: ', aliases);
    setPersonalizeExperiences(experiences);
  };

  const loadPersonalizeData = async (personalizeSdk) => {
    if (!pageData?.uid) return;
    if (pageData?.locale !== LOCALES.EN_US) return;

    try {
      const [personalizedData, hasConsent] = await Promise.all([
        PersonalizeService.fetchPersonalizeDataCSR(personalizeSdk, pageData.uid, search),
        OneTrustService.checkConsent(ONETRUST_CATEGORIES.targeting),
      ]);

      if (!hasConsent) {
        Logger.log('Personalize CSR: targeting consent not granted, skipping variant apply.');
        return;
      }

      if (!personalizedData) return;
      if (PersonalizeSDKService.hasTimeout()) return;

      applyPersonalizeResult(personalizedData, personalizeSdk);
    } catch (err) {
      Logger.error('Error initializing Personalize: ', err);
    }
  };

  const triggerPersonalizeEvent = useCallback(
    (eventName, uid) => {
      if (!isPersonalizeEnabled || !sdk) return;

      const trigger = clickEventTriggers.find((trigger) => trigger.uid === uid);
      if (!trigger) return;

      Logger.log(`Trigger Conversion Event: ${eventName} for element with UID: ${uid}`);
      personalizeExperiences.forEach(({ alias, experienceShortUid, variantShortUid, experience, variant }) => {
        AnalyticsService.pushEvent('ab_test_conversion', {
          conversion_action: eventName,
          variation_alias: alias,
          variation_id: experienceShortUid,
          experience_name: experience,
          variation_name: variant,
          active_variant: variantShortUid,
        });
      });
      void triggerEvent(eventName, sdk);
    },
    [sdk, clickEventTriggers, personalizeExperiences]
  );

  useEffect(() => {
    if (pageData) {
      ContentstackGatsby.addContentTypeUidFromTypename(pageData);
    }
    // Setup live preview
    if (isLivePreviewEnabled) {
      ContentstackLivePreview.onEntryChange(fetchLivePreviewData);
    }

    // Initialize personalization SDK
    const initializePersonalize = async () => {
      try {
        if (!isPersonalizeEnabled) {
          return; // Personalization globally disabled
        }

        const sdkInstance = await PersonalizeSDKService.waitForSdk();

        setSdk(sdkInstance);
        await loadPersonalizeData(sdkInstance);
      } catch (error) {
        if (error instanceof PersonalizeSDKError) {
          Logger.warn('Personalize SDK initialization failed:', error.message);
          return;
        }
        Logger.error('Error initializing personalization:', error);
      } finally {
        PersonalizeSDKService.clearTimer();
        setIsPersonalizeDataSettled(true);
      }
    };

    void initializePersonalize();

    return () => {
      if (isLivePreviewEnabled) {
        ContentstackLivePreview?.unsubscribeOnEntryChange(fetchLivePreviewData);
      }
    };
  }, []);

  useEffect(() => {
    setClickEventTriggers(abTestConfiguration?.click_event_triggers || []);
  }, []);

  const contextValue = useMemo(
    () => ({
      data,
      sdk,
      personalize: { experiences: personalizeExperiences },
      isPersonalizeDataSettled: isPersonalizeDataSettled,
      triggerPersonalizeEvent,
      shouldWaitForPersonalize:
        data?.ab_test_configuration?.wait_for_personalize ?? abTestConfiguration?.wait_for_personalize ?? false,
    }),
    [data, sdk, personalizeExperiences, isPersonalizeDataSettled, triggerPersonalizeEvent, abTestConfiguration]
  );

  return <ContentstackContext.Provider value={contextValue}>{children}</ContentstackContext.Provider>;
};

export const useContentstack = () => {
  const context = useContext(ContentstackContext);

  if (!context) {
    Logger.error('useContentstack must be inside ContentstackProvider');
    return {
      data: null,
      sdk: null,
      personalize: { experiences: [] },
      isPersonalizeDataSettled: false,
      shouldWaitForPersonalize: false,
      triggerPersonalizeEvent: () => {},
    };
  }

  return context;
};
